import { createContext, use, useState, useCallback, useEffect, type ReactNode } from "react";
import { trpc } from "@/trpc";
import { UserType } from "@satyrsmc/shared/client";
import type { AuthUser } from "@satyrsmc/shared/dto/auth";

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isWebmaster: boolean;
  isMember: boolean;
}

interface AuthActions {
  setUser: (user: AuthUser | null) => void; // also clears isLoading
  logout: () => Promise<void>;
}

type AuthContextValue = AuthState & AuthActions;

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const utils = trpc.useUtils();

  const logoutMutation = trpc.auth.logout.useMutation();

  // On mount: try me → if 401, give up (long-lived cookie handles re-auth)
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const userData = await utils.auth.me.fetch();
        if (!cancelled) {
          setUser(userData);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setIsLoading(false);
        }
      }
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
    setUser(null);
    await utils.invalidate();
  }, [logoutMutation, utils]);

  // Wrap setUser so calling it also marks loading as done
  const setUserAndReady = useCallback((u: AuthUser | null) => {
    setUser(u);
    setIsLoading(false);
  }, []);

  const isAuthenticated = user !== null;
  const isWebmaster = isAuthenticated && user.user_type === UserType.Webmaster;
  const isAdmin =
    isAuthenticated && (user.user_type === UserType.Admin || user.user_type === UserType.Webmaster);
  const isMember = isAuthenticated && (user.is_member || isAdmin);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated,
    isAdmin,
    isWebmaster,
    isMember,
    setUser: setUserAndReady,
    logout,
  };

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
